import React, { useState, useCallback, useEffect } from 'react';
import styles from '../Style/ValidationPanel.module.scss';
import SateliteTab from './SateliteTab';
import InSituTab from './InSituTab';
import pointsWithinPolygon from '@turf/points-within-polygon';
import { featureCollection, point, polygon } from '@turf/helpers'
import { Position } from 'deck.gl';
import { EVENT_VALIDATION_INSITU_SELECTED, EVENT_VALIDATION_INSITU_UPDATED, EVENT_VALIDATION_SATELLITE_SELECTED, trackUpdate } from '../../../../utils/matomoEvents';
import {
  DownloadCheckboxKey,
  INSITU_TAB,
  ITEM,
  ROSE_PLOT_VALIDATION,
  SATELLITE_TAB,
  SCATTER_PLOT_VALIDATION,
  TabKey,
  TS_PLOT_VALIDATION
} from '../../../../utils/common';
import { UserGroupPermissions } from '../../../../types/userGroupPermissions';
import { Divider, Radio, RadioChangeEvent } from 'antd';
import { useSubscriptionStatus } from '../../../../utils/hooks';
import useLegendStore from "../../../../store/legendStore";
import {useMapLayerStore} from "../../../../store/layerStore";
import {usePointStore} from "../../../../store/pointStore";
import {IMarker} from "../../../../types/marker";
import {BASIC_ANALYTICS, DEFAULT_ANALYTICS} from "../../../../types/analytics.d";

interface Props {
    userGroupPermissions: UserGroupPermissions;
    datasetId: string
    category: string
    cellSize: string
    waterDepth: string
    activePoint?: IMarker,
    activePlotValidation: string[],
    setActivePlotValidation: React.Dispatch<React.SetStateAction<string[]>>,
    onChangePointLocation: (coords: number[], forceChange?: boolean) => void,
    onMeshControl: (item: string) => void
    onChangeActiveKey: (item: any, tabKey: TabKey) => void
}

const ValidationPanel: React.FC<Props> = ({
    activePoint,
    cellSize,
    waterDepth,
    userGroupPermissions,
    datasetId,
    onChangePointLocation,
    onMeshControl,
    onChangeActiveKey
}) => {
    const showInSituPoints = useLegendStore(state => state.showInSituPoints);
    const setShowInSituPoints = useLegendStore(state => state.setShowInSituPoints);
    const datasetZones = useMapLayerStore(state => state.datasetZones);
    const inSituPoints = usePointStore(state => state.inSituPoints);

    const [disabledChart, setDisableChart] = useState(false)
    const { isActiveSubscriber } = useSubscriptionStatus();
    const [activeValidationTab, setActiveValidationTab] = useState(SATELLITE_TAB)
    const [activeInsituTab, setActiveInsituTab] = useState(isActiveSubscriber ? TS_PLOT_VALIDATION : SCATTER_PLOT_VALIDATION)
    const [activePlotValidation, setActivePlotValidation] = useState<string[]>([
      'Altimeter Comparison',
    ]);
    const [isInsituAvailable, setIsInsituAvailable] = useState(false)
    const selectedDatazone = datasetZones && datasetZones.find((item) => item.id === datasetId)

  const handleChangePoint = (item: any) => {
      onChangeActiveKey(item, ITEM.VALIDATION)
    }

    const isValidInsitu = useCallback(() => {
        if (showInSituPoints) {
            const tolerance = 0.000001;
            if (selectedDatazone) {
                const selectedPolygon = polygon([selectedDatazone.coordinates as Position[]]);
                const points = featureCollection(inSituPoints.map(({ position }) => point(position)));
                const pointsInside = pointsWithinPolygon(points, selectedPolygon);
                if (pointsInside.features.length > 0) {
                    setIsInsituAvailable(true)
                } else {
                    setIsInsituAvailable(false)
                }
            } else {
                setIsInsituAvailable(false)
            }
            const findInSitu = inSituPoints.find((item) => {
                if (activePoint?.coordinates) {
                    const itemX = item.position[0];
                    const itemY = item.position[1];
                    const activeX = activePoint.coordinates[0];
                    const activeY = activePoint.coordinates[1];

                    const isXCloseEnough = Math.abs(itemX - activeX) < tolerance;
                    const isYCloseEnough = Math.abs(itemY - activeY) < tolerance;

                    if (isXCloseEnough && isYCloseEnough) {
                        return true;
                    }
                }
                return false;
            });
            if (findInSitu) {
                setDisableChart(true)
            } else {
                setDisableChart(false)
            }
        }

    }, [inSituPoints, activePoint?.coordinates, showInSituPoints, datasetId])

    useEffect(() => {
        isValidInsitu();
    }, [activePoint?.coordinates, inSituPoints, showInSituPoints, datasetId]);

    const handleChangeTab = useCallback((key: string) => {
      if (activePoint?.coordinates.length && datasetId){
        const eventType = key === INSITU_TAB ? EVENT_VALIDATION_INSITU_SELECTED : EVENT_VALIDATION_SATELLITE_SELECTED;
        trackUpdate(eventType, activePoint?.coordinates, datasetId, key === INSITU_TAB ? DEFAULT_ANALYTICS : "")
      }
      if(key === INSITU_TAB ){
        setShowInSituPoints(true)
      }
      setActiveValidationTab(key)
    }, [activePoint?.coordinates, datasetId, activeValidationTab])

    const handleChangeInsituTab = useCallback((e: RadioChangeEvent) => {
      const value = e.target.value
      if (activePoint?.coordinates && activePoint?.dataset){
        const type = value === TS_PLOT_VALIDATION ? BASIC_ANALYTICS.TIME_SERIES.toLowerCase() : value === SCATTER_PLOT_VALIDATION ? BASIC_ANALYTICS.SCATTER_PLOT.toLowerCase() : BASIC_ANALYTICS.ROSE_PLOT.toLowerCase();
        trackUpdate(EVENT_VALIDATION_INSITU_UPDATED, activePoint?.coordinates, activePoint?.dataset, type)
      }
      setActiveInsituTab(value);
  }, [activePoint]);

    const headerSection = (
      <>
        <div className={styles.listNavigation}>
            <div
              className={activeValidationTab === SATELLITE_TAB ? styles.active : ''}
              key={SATELLITE_TAB}
              onClick={() => handleChangeTab(SATELLITE_TAB)}
            >
              Altimeter
            </div>
            <div
              className={activeValidationTab === INSITU_TAB ? styles.active : ''}
              key={INSITU_TAB}
              onClick={() => handleChangeTab(INSITU_TAB)}
              >
              In situ
            </div>
        </div>
        <div style={{borderBottom: '2px solid #f0f0f0', width: '95%', marginTop: 10, marginBottom: 10}} />
        {activeValidationTab === SATELLITE_TAB && (
          <Radio.Group
            onChange={() => null}
            value={SCATTER_PLOT_VALIDATION}
            size="small"
            style={{
              paddingLeft: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 6
            }}>
            <Radio disabled>Time Series Plot</Radio>
            <Radio value={SCATTER_PLOT_VALIDATION} >Scatter Plot</Radio>
            <Radio disabled >Rose Plot</Radio>
          </Radio.Group>
        )}
        {activeValidationTab === INSITU_TAB && (
          <Radio.Group
            onChange={handleChangeInsituTab}
            value={activeInsituTab}
            size="small"
            style={{
              paddingLeft: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 6
            }}>
            <Radio value={TS_PLOT_VALIDATION} disabled={!isActiveSubscriber}>Time Series Plot</Radio>
            <Radio value={SCATTER_PLOT_VALIDATION}>Scatter Plot</Radio>
            <Radio value={ROSE_PLOT_VALIDATION} disabled >Rose Plot</Radio>
          </Radio.Group>
        )}
      </>
    )

    return (
      <div className={styles.validationPanel}>
        <div className={styles.contentSection}>
          <>
            <div style={{display: activeValidationTab === INSITU_TAB ? 'block' : 'none', height: '100%' }}>
              <InSituTab
                cellSize={cellSize}
                datasetId={datasetId}
                userGroupPermissions={userGroupPermissions}
                waterDepth={waterDepth}
                activePoint={activePoint}
                validInSituPoint={disabledChart}
                isInsituAvailable={isInsituAvailable}
                onChangePointLocation={onChangePointLocation}
                onChangeActiveKey={handleChangePoint}
                activePanel={activeValidationTab}
                headerSection={headerSection}
                activeInsituTab={activeInsituTab}
                onMeshControl={onMeshControl}
                />
            </div>
            <div style={{display: activeValidationTab === SATELLITE_TAB ? 'block' : 'none', height: '100%' }}>
              <SateliteTab
                activePointId={activePoint?.id!}
                datasetKey={activePoint?.activeDatasetKey ?? DownloadCheckboxKey.SW}
                downloadItems={activePoint?.downloadPage!}
                coordinates={activePoint?.coordinates!}
                previewDatasetId={datasetId ?? ''}
                userGroupPermissions={userGroupPermissions!}
                pointName={activePoint?.label ?? ''}
                gridCellSize={cellSize}
                waterDepth={waterDepth}
                activePlot={activePlotValidation}
                setActivePlot={setActivePlotValidation}
                activePanel={activeValidationTab}
                headerSection={headerSection}
                onChangeActiveKey={handleChangePoint}
              />
            </div>
          </>
        </div>
      </div>
    );
};

export default ValidationPanel;
